import { serverTimestamp } from "firebase/firestore";

/**
 * Utility function to convert month name to 1-based integer index
 * @param {string|number} month - Month name (e.g., 'August', 'September') or existing number
 * @returns {number} 1-based month index (1-12)
 */
const monthNameToInt = (month) => {
    // If it's already a number, validate and return it
    if (typeof month === 'number') {
        return Math.max(1, Math.min(12, Math.floor(month)));
    }
    
    // If it's a string number, parse it
    if (typeof month === 'string') {
        const parsed = parseInt(month, 10);
        if (!isNaN(parsed)) {
            return Math.max(1, Math.min(12, parsed));
        }
        
        // Handle month names (case-insensitive)
        const monthNames = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'
        ];
        
        const monthIndex = monthNames.indexOf(month.toLowerCase());
        if (monthIndex !== -1) {
            return monthIndex + 1; // Convert to 1-based index
        }
    }
    
    // Default to current month if invalid
    return new Date().getMonth() + 1;
};

/**
 * Fee Due Model
 * Represents a fee due record from Firestore
 */
export class FeeDue {
    constructor({
        admissionNo = '',
        amount = 0,
        date = null,
        dueParticulars = '',
        month = '',
        id = null
    } = {}) {
        this.admissionNo = admissionNo;
        this.amount = parseFloat(amount) || 0;
        this.date = date; // Will be converted to JS Date in fromMap
        this.dueParticulars = dueParticulars;
        this.month = monthNameToInt(month); // Convert month name to 1-based integer
        this.id = id;
    }

    /**
     * Creates a FeeDue instance from Firestore document data
     * @param {Object} data - Firestore document data
     * @param {string} docId - Document ID
     * @returns {FeeDue} FeeDue instance
     */
    static fromMap(data, docId = null) {
        if (!data) {
            return new FeeDue();
        }

        // Convert Firestore Timestamp to JavaScript Date
        let dateValue = null;
        if (data.date) {
            // Check if it's a Firestore Timestamp
            if (data.date.toDate && typeof data.date.toDate === 'function') {
                dateValue = data.date.toDate();
            } else if (data.date instanceof Date) {
                dateValue = data.date;
            } else if (typeof data.date === 'string') {
                dateValue = new Date(data.date);
            }
        }

        return new FeeDue({
            admissionNo: data.admissionNo,
            amount: data.amount,
            date: dateValue,
            dueParticulars: data.dueParticulars,
            month: data.month,
            id: docId
        });
    }

    /**
     * Converts the FeeDue instance to a plain object for Firestore
     * @returns {Object} Plain object representation
     */
    toMap() {
        return {
            admissionNo: this.admissionNo,
            amount: this.amount,
            date: serverTimestamp(),
            dueParticulars: this.dueParticulars,
            month: this.month
        };
    }

    /**
     * Gets formatted amount with currency symbol
     * @returns {string} Formatted amount
     */
    getFormattedAmount() {
        return `₹${this.amount.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    }

    /**
     * Gets formatted date string
     * @returns {string} Formatted date
     */
    getFormattedDate() {
        if (!this.date) return 'N/A';
        return this.date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    /**
     * Gets formatted month display
     * @returns {string} Formatted month
     */
    getFormattedMonth() {
        if (!this.month) return 'N/A';
        
        // If month is a number, convert to month name
        if (typeof this.month === 'number') {
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            return monthNames[this.month - 1] || this.month.toString();
        }
        
        return this.month;
    }

    /**
     * Gets due particulars display with proper formatting
     * @returns {string} Formatted due particulars
     */
    getDueParticularsDisplay() {
        if (!this.dueParticulars) return 'N/A';
        
        // If it's an array, join with commas
        if (Array.isArray(this.dueParticulars)) {
            return this.dueParticulars.join(', ');
        }
        
        // If it's an object, extract relevant information
        if (typeof this.dueParticulars === 'object') {
            // Try to extract common fields
            const fields = [];
            if (this.dueParticulars.tuitionFee) fields.push(`Tuition: ₹${this.dueParticulars.tuitionFee}`);
            if (this.dueParticulars.transportFee) fields.push(`Transport: ₹${this.dueParticulars.transportFee}`);
            if (this.dueParticulars.examFee) fields.push(`Exam: ₹${this.dueParticulars.examFee}`);
            if (this.dueParticulars.libraryFee) fields.push(`Library: ₹${this.dueParticulars.libraryFee}`);
            if (this.dueParticulars.other) fields.push(`Other: ₹${this.dueParticulars.other}`);
            
            return fields.length > 0 ? fields.join(', ') : JSON.stringify(this.dueParticulars);
        }
        
        return this.dueParticulars.toString();
    }

    /**
     * Checks if the fee is overdue
     * @returns {boolean} True if overdue
     */
    isOverdue() {
        if (!this.date) return false;
        return this.date < new Date();
    }

    /**
     * Gets the number of days overdue (negative if not yet due)
     * @returns {number} Days overdue
     */
    getDaysOverdue() {
        if (!this.date) return 0;
        const today = new Date();
        const diffTime = today - this.date;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
}

export default FeeDue;
